import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useCallback, createContext, } from 'react';
import { cleanGoogleRecaptcha, injectGoogleReCaptchaScript, logWarningMessage, } from './utils';
var GoogleRecaptchaError;
(function (GoogleRecaptchaError) {
    GoogleRecaptchaError["SCRIPT_NOT_AVAILABLE"] = "Recaptcha script is not available";
})(GoogleRecaptchaError || (GoogleRecaptchaError = {}));
const GoogleReCaptchaContext = createContext({
    executeRecaptcha: () => {
        throw Error('GoogleReCaptcha Context has not yet been implemented, if you are using useGoogleReCaptcha hook, make sure the hook is called inside component wrapped by GoogleRecaptchaProvider');
    },
});
const { Consumer: GoogleReCaptchaConsumer } = GoogleReCaptchaContext;
export var SCRIPT_STATE;
(function (SCRIPT_STATE) {
    SCRIPT_STATE["NONE"] = "NONE";
    SCRIPT_STATE["LOADING"] = "LOADING";
    SCRIPT_STATE["LOADED"] = "LOADED";
    SCRIPT_STATE["FAILED"] = "FAILED";
})(SCRIPT_STATE || (SCRIPT_STATE = {}));
export function GoogleReCaptchaProvider({ reCaptchaKey, useEnterprise = false, useRecaptchaNet = false, loadOnDemand = false, scriptProps, language, children, }) {
    const [greCaptchaInstance, setGreCaptchaInstance] = useState(null);
    const [isLoadOnDemand, setIsLoadOnDemand] = useState(loadOnDemand);
    const [scriptLoadState, setScriptLoadState] = useState(SCRIPT_STATE.NONE);
    const scriptId = scriptProps?.id || 'google-recaptcha-v3';
    const loadRecaptchaScript = () => {
        return new Promise((resolve, _) => {
            const onLoad = () => {
                if (!window || !window.grecaptcha) {
                    logWarningMessage(`<GoogleRecaptchaProvider /> ${GoogleRecaptchaError.SCRIPT_NOT_AVAILABLE}`);
                    return;
                }
                const grecaptcha = useEnterprise
                    ? window.grecaptcha.enterprise
                    : window.grecaptcha;
                grecaptcha.ready(() => {
                    setGreCaptchaInstance(grecaptcha);
                    setScriptLoadState(SCRIPT_STATE.LOADED);
                    resolve(grecaptcha);
                });
            };
            const onError = () => {
                logWarningMessage('Error loading google recaptcha script');
                setScriptLoadState(SCRIPT_STATE.FAILED);
            };
            setScriptLoadState(SCRIPT_STATE.LOADING);
            injectGoogleReCaptchaScript({
                reCaptchaKey,
                useEnterprise,
                useRecaptchaNet,
                scriptProps,
                language,
                onLoad,
                onError,
            });
        });
    };
    useEffect(() => {
        if (!reCaptchaKey) {
            logWarningMessage('<GoogleReCaptchaProvider /> recaptcha key not provided');
            return;
        }
        if (!loadOnDemand) {
            loadRecaptchaScript();
        }
        return () => {
            cleanGoogleRecaptcha(scriptId);
        };
    }, [useEnterprise, useRecaptchaNet, scriptProps, language, reCaptchaKey]);
    const executeRecaptcha = useCallback(async (action) => {
        if (!greCaptchaInstance || !greCaptchaInstance.execute) {
            if (loadOnDemand) {
                return loadRecaptchaScript().then(grecaptcha => {
                    return grecaptcha.execute(reCaptchaKey, { action });
                });
            }
            else {
                throw new Error('<GoogleReCaptchaProvider /> Google Recaptcha has not been loaded');
            }
        }
        const result = await greCaptchaInstance.execute(reCaptchaKey, { action });
        return result;
    }, [greCaptchaInstance, scriptLoadState, loadOnDemand]);
    const googleReCaptchaContextValue = useMemo(() => ({
        executeRecaptcha: greCaptchaInstance || loadOnDemand ? executeRecaptcha : undefined,
        loadOnDemand,
        loadRecaptcha: loadRecaptchaScript,
    }), [executeRecaptcha, greCaptchaInstance, loadOnDemand]);
    return (_jsx(GoogleReCaptchaContext.Provider, { value: googleReCaptchaContextValue, children: children }));
}
export { GoogleReCaptchaConsumer, GoogleReCaptchaContext };
